import { Card, Row, Spin } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { KPI_CODES } from "../../kpiCodes";
import { StatInfo } from "./components/StatInfo";
import { YearSelector } from "./components/YearSelector";
import { useFetchData } from "./hooks/useFetchData";

type TypicalAccountSetupProps = {
  data: any;
  isLoading: boolean;
};

export const TypicalAccountSetup = ({
  data,
  isLoading,
}: TypicalAccountSetupProps) => {
  const [date, setDate] = useState<string>(`${dayjs().year() - 1}-12-31`);

  const getKpiByKey = useCallback(
    (key: string, values: any): any =>
      values
        ? values.kpiValues.find((item: any) => item.code === key)
        : undefined,
    [],
  );

  const { isLoading: isLoadingData, data: yearData } = useFetchData(
    date,
    getKpiByKey,
  );

  const beginningBalance: number = useMemo(
    () => getKpiByKey(KPI_CODES.ACCOUNTS_BEGINNING_BALANCE, data)?.value,
    [getKpiByKey, data],
  );

  const interestRate: number = useMemo(
    () => getKpiByKey(KPI_CODES.ACCOUNTS_INTEREST_RATE, data)?.value / 100,
    [getKpiByKey, data],
  );

  const regularPayment: number = useMemo(
    () => getKpiByKey(KPI_CODES.ACCOUNTS_REGULAR_PAYMENT, data)?.value,
    [getKpiByKey, data],
  );

  const accountsWithImpounds: number = useMemo(
    () => getKpiByKey(KPI_CODES.ACCOUNTS_WITH_IMPOUNDS, data)?.value,
    [getKpiByKey, data],
  );

  const onSelectYear = useCallback((val: string) => setDate(val), []);

  return (
    <Card className="dashboard-card top">
      <Row className="justify-space-between align-center">
        <p className="text my-0">Typical Account Setup</p>
        <YearSelector value={date} onSelect={onSelectYear} />
      </Row>
      {!isLoading && !isLoadingData ? (
        <>
          <Row className="w-100">
            <StatInfo
              origin={beginningBalance}
              valueToCompare={yearData.beginningBalance}
              date={date}
              label="Beginning Balance"
            />
            <StatInfo
              origin={interestRate}
              valueToCompare={yearData.interestRate}
              date={date}
              label="Interest Rate"
              isPercent
            />
            <StatInfo
              origin={regularPayment}
              valueToCompare={yearData.regularPayment}
              date={date}
              label="Regular Payment"
            />
            <StatInfo
              origin={accountsWithImpounds}
              valueToCompare={yearData.accountsWithImpounds}
              date={date}
              label="Has Impound"
              isPercent
              showDigits={false}
            />
          </Row>
        </>
      ) : (
        <Row className="h-100 justify-center align-center">
          <Spin size="large" />
        </Row>
      )}
    </Card>
  );
};
